import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import { useEffect, useRef, useState } from "react";
import { useGetKnowledgeBaseQuery } from "../../service/main";
import { Article } from "../../types/General";
import Loader from "../../constants/Loader";
import CustomPagination from "../../components/CustomPagination";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import CircularProgress from "@mui/material/CircularProgress";
import { useToggleKnowledgeLikeMutation, useGetKnowledgeResourcesQuery } from "../../service/main";


const DescriptionWithReadMore = ({ description }: { description: string }) => {
    const [showFull, setShowFull] = useState(false);
    const [showReadMore, setShowReadMore] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (ref.current) {
            const isLong = ref.current.scrollHeight > ref.current.clientHeight;
            setShowReadMore(isLong);
        }
    }, [description]);

    return (
        <div>
            <div
                ref={ref}
                className={`description-container ${showFull ? "open" : ""}`}
                dangerouslySetInnerHTML={{ __html: description }}
            />
            {showReadMore && (
                <span
                    style={{
                        color: "#d3b88c",
                        cursor: "pointer",
                        fontSize: "14px",
                        display: "inline-block",
                        marginTop: "8px",
                        fontWeight: 500,
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowFull(!showFull);
                    }}
                >
                    {showFull ? "Show Less" : "Read More"}
                </span>
            )}
        </div>
    );
};

const formatReadTime = (seconds: string | number) => {
    const totalSeconds = Number(seconds);
    if (isNaN(totalSeconds) || totalSeconds <= 0) return "0 seconds";

    const hrs = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const parts = [];
    if (hrs > 0) parts.push(`${hrs} hour${hrs > 1 ? "s" : ""}`);
    if (mins > 0) parts.push(`${mins} minute${mins > 1 ? "s" : ""}`);
    if (secs > 0 || parts.length === 0)
        parts.push(`${secs} second${secs > 1 ? "s" : ""}`);

    return parts.join(" ");
};

const KnowledgeCard = ({ item, onClick }: { item: any, onClick: () => void }) => {
    const [isLiked, setIsLiked] = useState(item?.isLiked ?? false);
    const [likeCount, setLikeCount] = useState(item?.likeCount ?? 0);
    const [toggleKnowledgeLike, { isLoading: isLiking }] = useToggleKnowledgeLikeMutation();

    const handleKnowledgeLike = async (e: React.MouseEvent) => {
        e.stopPropagation();
        const prevIsLiked = isLiked;
        const prevCount = likeCount;

        setIsLiked(!prevIsLiked);
        setLikeCount(prevIsLiked ? prevCount - 1 : prevCount + 1);

        try {
            await toggleKnowledgeLike(item.id).unwrap();
        } catch (error) {
            console.error(error);
            setIsLiked(prevIsLiked);
            setLikeCount(prevCount);
        }
    };

    return (
        <div className="help-card help_view_card" onClick={onClick}>
            <div className="help-icon">
                <img src={item?.icon} alt="" />
            </div>

            <div>
                <h4>{item.title}</h4>
                <DescriptionWithReadMore description={item?.description} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px' }}>
                    <span className="time">
                        {formatReadTime(item?.readTime)}
                    </span>
                    <IconButton disabled={isLiking} onClick={handleKnowledgeLike} size="small" style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', color: '#666' }}>
                        {isLiked ? <ThumbUpIcon sx={{ color: '#d3b88c' }} fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
                        {likeCount > 0 && <span>{likeCount}</span>}
                    </IconButton>
                </div>
            </div>
        </div>
    );
};

const KnowledgeBase = () => {
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
    const navigate = useNavigate();

    const { data: knowledgeDetailData, isFetching: isDetailLoading } = useGetKnowledgeResourcesQuery(selectedArticle?.id || "", { skip: !selectedArticle });
    const detailArticle = knowledgeDetailData?.data || selectedArticle;

    const { data: knowledgeBase, isLoading } = useGetKnowledgeBaseQuery({
        page,
        limit: 10,
        ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
    });

    const allData: Article[] = knowledgeBase?.data?.list || [];
    
    const knowledgeData = debouncedSearchTerm
        ? allData.filter((item) =>
            item.title?.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
            item.description?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
        )
        : allData;
    const count = knowledgeBase?.data?.total || knowledgeData.length || 0;

    useEffect(() => {
        setPage(1);
    }, [debouncedSearchTerm]);

    return (
        <>
            <Loader isLoad={isLoading} />
            <div className="main_title">
                <h1>Knowledge Base View</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
                    <button onClick={() => navigate("/admin-support")}>
                        Admin Support Management
                    </button>
                    - Knowledge Base
                </p>
            </div>
            <div className="cards">
                <div className="table_header">
                    <div className="left_s">
                        <SearchBar
                            value={searchTerm}
                            onChange={(e: any) => setSearchTerm(e.target.value)}
                            onCross={() => {
                                setSearchTerm("");
                                setDebouncedSearchTerm("");
                                setPage(1);
                            }}
                            searchTerm={searchTerm}
                            setDebouncedSearchTerm={setDebouncedSearchTerm}
                            placeholder="Search..."
                        />
                    </div>
                </div>
                <div className="help-center-grid">
                    {knowledgeData?.length
                        ? knowledgeData?.map((item, i) => (
                            <KnowledgeCard key={i} item={item} onClick={() => setSelectedArticle(item)} />
                        ))
                        : null}
                </div>
                {count > 0 ? (
                    <CustomPagination
                        currentPage={page - 1}
                        count={count}
                        pageSize={10}
                        onPageChange={(p) => setPage(p + 1)}
                    />
                ) : null}
            </div>
            <Modal
                open={!!selectedArticle}
                onClose={() => setSelectedArticle(null)}
                aria-labelledby="knowledge-base-modal-title"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 600,
                        maxWidth: "95%",
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 3,
                        outline: "none",
                        maxHeight: "90vh",
                        overflowY: "auto",
                    }}
                >
                    <IconButton
                        onClick={() => setSelectedArticle(null)}
                        sx={{ position: "absolute", right: 15, top: 15 }}
                    >
                        <CloseIcon />
                    </IconButton>
                    
                    {isDetailLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
                            <CircularProgress />
                        </div>
                    ) : detailArticle && (
                        <>
                            {detailArticle.icon && (
                                <img
                                    src={detailArticle.icon}
                                    alt={detailArticle.title}
                                    style={{
                                        width: "100%",
                                        maxHeight: "300px",
                                        objectFit: "cover",
                                        borderRadius: "8px",
                                        marginBottom: "20px"
                                    }}
                                />
                            )}
                            <h2 id="knowledge-base-modal-title" style={{ margin: "0 0 15px 0", paddingRight: "30px", fontSize: "22px", color: "#222" }}>
                                {detailArticle.title}
                            </h2>
                            
                            <div style={{ marginBottom: "20px", color: "#555", fontSize: "16px", lineHeight: "1.6" }} dangerouslySetInnerHTML={{ __html: detailArticle.description }} />
                            
                            <span style={{ color: "#888", fontSize: "14px", fontWeight: 500 }}>
                                Read Time: {formatReadTime(detailArticle.readTime)}
                            </span>
                        </>
                    )}
                </Box>
            </Modal>
        </>
    );
};
export default KnowledgeBase;
